import { portalText } from "../../i18n/portal.js";
/**
 * What a role change takes away, in the words the confirmation uses (customer-portal review
 * 2026-09-25 #158: a role box saved the moment it changed, so one slip demoted an Owner).
 *
 * AMENDED 2026-10-06 (plan-014 B3): the seven workspace roles are not a ladder — a Developer and a Campaign manager
 * each hold something the other lacks — so "fewer rights" is asked of the capability matrix itself
 * (`WORKSPACE_CAPABILITY_ROLE`, the one the server and the database enforce): a change takes rights away when the
 * old role holds a capability the new one does not. The rights are named in groups, in the words the Workspace page's
 * role summary uses.
 */
import { WORKSPACE_CAPABILITY_ROLE, holdsWorkspaceCapability, type WorkspaceCapability } from "@wetalk/domain";

import type { MemberRoleView } from "../../api/gateway.js";

/** The rights a confirmation names, each the capabilities it stands for, in the order they are listed. */
const RIGHT: readonly { readonly words: string; readonly capability: readonly WorkspaceCapability[] }[] = [
  { get words() { return portalText("core", "workspaces.manage_billing_0d6c1b"); }, capability: ["workspace.lifecycle"] },
  { get words() { return portalText("core", "workspaces.manage_people_channels_and_api_keys_898066"); }, capability: ["membership.invite", "membership.role_change", "membership.remove", "api_key.manage"] },
  { get words() { return portalText("core", "workspaces.run_and_test_agents_e26128"); }, capability: ["voice_agent.write", "agent_document.write", "channel_binding.write", "webhook_endpoint.manage"] },
  { get words() { return portalText("core", "workspaces.run_campaigns_7d5dd3"); }, capability: ["campaign.operate", "contact_list.manage", "do_not_call_entry.add", "export.campaign"] },
  { get words() { return portalText("core", "workspaces.issue_api_keys_for_integrations_478ff3"); }, capability: ["api_key.integration_manage"] },
  { get words() { return portalText("core", "workspaces.read_transcripts_86dede"); }, capability: ["transcript.read"] },
];

const EVERY_CAPABILITY = Object.keys(WORKSPACE_CAPABILITY_ROLE) as WorkspaceCapability[];

function lostCapabilities(from: MemberRoleView, to: MemberRoleView): readonly WorkspaceCapability[] {
  return EVERY_CAPABILITY.filter((one) => holdsWorkspaceCapability(from, one) && !holdsWorkspaceCapability(to, one));
}

/** True when `to` lacks something `from` holds: the change is asked about first. */
export function removesRights(from: MemberRoleView, to: MemberRoleView): boolean {
  return lostCapabilities(from, to).length > 0;
}

/** The rights lost, as a phrase: "manage billing, manage people, channels and API keys or run and test agents". */
export function rightsLostBy(from: MemberRoleView, to: MemberRoleView): string {
  const lost = new Set(lostCapabilities(from, to));
  const named = RIGHT.filter((one) => one.capability.some((capability) => lost.has(capability))).map((one) => one.words);
  if (named.length === 0) return lost.size === 0 ? "" : portalText("core", "workspaces.do_some_of_what_they_can_do_now_ba6493");
  if (named.length === 1) return named.join("");
  return portalText("core", "workspaces._or_ffe470", { value0: named.slice(0, -1).join(", "), value1: named.at(-1) ?? "" });
}

/** "an Owner", "a Campaign manager". */
export function roleWithArticle(role: MemberRoleView): string {
  return {
    owner: portalText("core", "auth.an_owner_d8d838"),
    admin: portalText("core", "auth.an_admin_9df3ef"),
    member: portalText("core", "auth.a_member_2a7062"),
    viewer: portalText("core", "auth.a_viewer_ad521b"),
    developer: portalText("core", "auth.a_developer_8aecdf"),
    campaign_manager: portalText("core", "auth.a_campaign_manager_0ce110"),
    billing: portalText("core", "auth.a_billing_member_9bc4b9"),
  }[role];
}
